import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { useIdentityStore } from "@/data/stores/identity";

import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { OperatorDao } from "@/data/dao/service/OperatorDao";
import { ServiceType } from "@/data/model/Enums";
import type { ILocation } from "@/data/model/Location";
import type { IRoomPlanTable } from "@/data/model/Operator";
import type { IRoomWithService } from "@/data/model/Room";
import type { IService } from "@/data/model/Service";
import type { ITableAllocation } from "@/data/model/Table";
import { DateTime } from "@/data/util/DateTime";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { getLocationName } from "@/shared/helpers/LocationHelper";

export const useTableAllocationStore = defineStore("tableAllocation", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);

    // stores
    const identity = useIdentityStore();

    // state
    const allocation = ref<ITableAllocation | null>(null);
    const location = ref<ILocation | null>(null);
    const room = ref<IRoomWithService | null>(null);
    const orderServices = ref<IService[]>([]);
    const paymentServices = ref<IService[]>([]);
    const isLoading = ref<boolean>(false);
    const isInitializedFullTableAllocationInfo = ref<boolean>(false);
    const isLoadingFullTableAllocationInfo = ref<boolean>(true);
    const fullTableInfo = ref<IRoomPlanTable>();

    // getters
    const fullTableName = computed(() => {
        let fullName = '';
        if (location.value != null) {
            fullName = getLocationName(location.value);
        }
        
        if (allocation.value?.roomName) {
            fullName += ` - ${allocation.value?.roomName}`;
        }
        if (allocation.value?.tableName) {
            fullName += ` - ${allocation.value?.tableName}`;
        }
        return fullName;
    });
    const hasOrderServices = computed(() => orderServices.value.length > 0);
    const hasPaymentServices = computed(() => paymentServices.value.length > 0);
    const isSingleService = computed(() => allocation.value?.services.length == 1);

    const tableId = computed(() => allocation.value?.tableId);
    const locationId = computed(() => allocation.value?.locationId);
    const roomId = computed(() => allocation.value?.roomId);
    const roomName = computed(() => allocation.value?.roomName);
    const orderBillId = computed(() => allocation.value?.orderBillId);

    // full table info
    const hasAnyGuests = computed(() => fullTableInfo.value != null && fullTableInfo.value.allocations > 0);
    const tableAllocationTimestamp = computed(() => {
        if (fullTableInfo.value != null && fullTableInfo.value.timestamp != null) {
            return DateTime.toTimeString(fullTableInfo.value.timestamp);
        }
        return null;
    });

    const guestsNumber = computed(() => fullTableInfo.value?.allocations);
    const tableStatus = computed(() => fullTableInfo.value?.status);
    const hash = computed(() =>
        JSON.stringify({
            locationId: allocation.value?.locationId,
            tableId: allocation.value?.tableId,
            orderBillId: allocation.value?.orderBillId,
        })
    );

    // actions
    // todo: this is too operator bound, find more general way for initialization
    function initalize(currentLocation: ILocation | null, currentRoom: IRoomWithService | null) {
        location.value = currentLocation;
        room.value = currentRoom;
    }

    async function allocateToTable(
        locationId: string,
        tableId: string,
        deviceFingerprint: string,
        orderBillId?: string
    ) {
        isLoading.value = true;

        try {
            const tableAllocation = await customerDao.allocateOperatorToTable({
                locationId: locationId,
                tableId: tableId,
                billId: orderBillId,
                deviceFingerprint: deviceFingerprint,
            });

            await setCurrentAllocation(tableAllocation);
        } catch (ex) {
            logError(ex);
            throw ex;
        } finally {
            isLoading.value = false;
        }
    }

    async function refreshAllocationInfo() {
        if (locationId.value == null || tableId.value == null) {
            console.log("Skip refreshing allocation info, locationId or tableId is null");
            return;
        }

        try {
            const tableAllocation = await customerDao.getCurrentAllocation();
            await setCurrentAllocation(tableAllocation);
        } catch (ex) {
            logError(ex);
        }
    }

    async function setCurrentAllocation(tableAllocation: ITableAllocation | null) {
        allocation.value = tableAllocation;
        orderServices.value = getServices(ServiceType.Order);
        paymentServices.value = getServices(ServiceType.Pay);
    }

    function getServices(serviceType: ServiceType) {
        const mapped = allocation.value?.services.filter((c) => c.serviceType === serviceType) ?? [];
        const sorted = mapped.sort((item1, item2) => item1.order - item2.order);
        return sorted;
    }

    async function loadGuestsInfo() {
        if (locationId.value == null || tableId.value == null) {
            console.log("Skip loading guests info, locationId or tableId is null");
            fullTableInfo.value = undefined;
            return;
        }

        // todo: not required anymore?
        if (!identity.hasOperatorAccess) {
            console.log("Skip loading guests info - user has no operator access");
            fullTableInfo.value = undefined;
            return;
        }

        isLoadingFullTableAllocationInfo.value = true;

        try {
            fullTableInfo.value = await operatorDao.getTableDetails({
                tableId: tableId.value!,
                orderBillId: orderBillId.value,
            });
        } catch (ex) {
            logError(ex);
            throw translations.roomPlan.failedLoadingTables;
        } finally {
            isLoadingFullTableAllocationInfo.value = false;
            isInitializedFullTableAllocationInfo.value = true;
        }
    }

    async function removeGuests() {
        const result = await operatorDao.removeGuests({ tableId: tableId.value! });
        await refreshGuestsInfo();
        return result;
    }

    async function refreshGuestsInfo() {
        await loadGuestsInfo();
    }

    function isAllocatedToTable(tableId: string) {
        return allocation.value?.tableId === tableId;
    }

    // watchers
    watch(locationId, async (newValue, oldValue) => {
        if (newValue !== oldValue) {
            await loadGuestsInfo();
        }
    });

    return {
        // state
        allocation,
        location,
        room,
        orderServices,
        paymentServices,
        isLoading,
        isInitializedFullTableAllocationInfo,
        isLoadingFullTableAllocationInfo,
        hasOrderServices,
        hasPaymentServices,
        hash,

        // getters
        fullTableName,
        fullTableInfo,
        hasAnyGuests,
        tableAllocationTimestamp,
        guestsNumber,
        locationId,
        roomId,
        roomName,
        tableId,
        tableStatus,
        isSingleService,

        // actions
        initalize,
        setCurrentAllocation,
        allocateToTable,
        loadGuestsInfo,
        refreshGuestsInfo,
        removeGuests,
        isAllocatedToTable,
        refreshAllocationInfo,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTableAllocationStore, import.meta.hot));
}
